import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './Tooltip.css'

/**
 * The app's own tooltip for an icon-only control.
 *
 * `title` was doing this job, and doing it badly: the browser waits about a second before
 * showing anything, paints it in the operating system's font on the operating system's
 * chrome, ignores the page's direction, and never appears at all for a keyboard user. On a
 * row of three unlabelled glyphs that means the labels are effectively absent — which is
 * why "is this preview or delete?" was a fair question of a row of grey icons.
 *
 * This appears at once, in the app's type, on hover AND on focus, and it is portalled and
 * fixed-positioned so no table cell or sheet can clip it. It is presentation only: the
 * accessible name stays on the control as `aria-label`, so nothing depends on the bubble
 * being rendered.
 *
 * @param {import('react').RefObject<HTMLElement>} anchorRef  what it points at
 * @param {string} label
 * @param {boolean} open
 */
export default function Tooltip({ anchorRef, label, open }) {
  const [position, setPosition] = useState(null)
  const frame = useRef(0)

  const place = useCallback(() => {
    const element = anchorRef.current
    if (!element) return
    const rect = element.getBoundingClientRect()
    /* Below when there is no room above — the first row of a table sits under a sticky
       header, and a bubble drawn over it cannot be read. */
    const below = rect.top < 64
    setPosition({
      left: Math.round(rect.left + rect.width / 2),
      top: Math.round(below ? rect.bottom : rect.top),
      placement: below ? 'below' : 'above',
    })
  }, [anchorRef])

  useEffect(() => {
    if (!open) {
      setPosition(null)
      return undefined
    }

    place()
    /* The row can scroll under the pointer — inside a sheet, or with the page — so the
       bubble follows rather than hanging in the air where the control used to be. */
    const follow = () => {
      cancelAnimationFrame(frame.current)
      frame.current = requestAnimationFrame(place)
    }
    window.addEventListener('scroll', follow, true)
    window.addEventListener('resize', follow)
    return () => {
      cancelAnimationFrame(frame.current)
      window.removeEventListener('scroll', follow, true)
      window.removeEventListener('resize', follow)
    }
  }, [open, place])

  if (!open || !position || !label) return null

  return createPortal(
    <span
      role="tooltip"
      className={`tooltip tooltip--${position.placement}`}
      style={{ top: position.top, left: position.left }}
    >
      <bdi>{label}</bdi>
    </span>,
    document.body,
  )
}
